// ⚠️ STUB matching `@geosoft/catalog-sdk` API draft. Replace with the published
// package per RFC-001.

import type { Layer } from '../viewer-sdk';

export interface CatalogClientOptions {
  baseUrl: string;
  getToken?: () => Promise<string | null>;
}

export interface SearchParams {
  bbox?: [number, number, number, number];
  datetime?: string;
  collections?: string[];
  limit?: number;
}

export interface CatalogClientOptionsExt extends CatalogClientOptions {
  tenantId?: string;
  fetchImpl?: typeof fetch;
}

export class CatalogClient {
  readonly baseUrl: string;
  readonly tenantId: string;
  private readonly fetchImpl: typeof fetch;
  private readonly getToken?: () => Promise<string | null>;
  private demoCache: Layer[] | null = null;

  constructor(opts: CatalogClientOptionsExt) {
    this.baseUrl = opts.baseUrl.replace(/\/+$/, '');
    this.tenantId = opts.tenantId ?? 'tnt-default';
    this.fetchImpl = opts.fetchImpl ?? ((...args) => fetch(...args));
    this.getToken = opts.getToken;
  }

  /**
   * Fetches the layer catalog from the backend's `/api/v1/catalog/layers`
   * endpoint. Falls back to SAMPLE_LAYERS on any network error so dev never
   * breaks if the API is down.
   */
  async search(_params: SearchParams = {}): Promise<Layer[]> {
    const demoLayers = await this.loadDemoLayers();
    try {
      const token = this.getToken ? await this.getToken() : null;
      const headers: Record<string, string> = {
        'X-Tenant-Id': this.tenantId,
        accept: 'application/json',
      };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await this.fetchImpl(`${this.baseUrl}/api/v1/catalog/layers`, {
        headers,
      });
      if (!res.ok) throw new Error(`catalog HTTP ${res.status}`);
      const layers = (await res.json()) as Layer[];
      if (Array.isArray(layers) && layers.length > 0) {
        return dedupeLayers([...layers, ...demoLayers]);
      }
      return dedupeLayers([...SAMPLE_LAYERS, ...demoLayers]);
    } catch (err) {
      console.warn('[catalog-sdk] backend unreachable, using SAMPLE_LAYERS', err);
      return dedupeLayers([...SAMPLE_LAYERS, ...demoLayers]);
    }
  }

  async get(id: string): Promise<Layer | null> {
    const layers = await this.search();
    return layers.find((layer) => layer.id === id) ?? null;
  }

  private async loadDemoLayers(): Promise<Layer[]> {
    if (this.demoCache) return this.demoCache;
    if (typeof DOMParser === 'undefined') return [];
    const minioBase = (import.meta.env.VITE_MINIO_BASE_URL ?? 'http://localhost:9000').replace(
      /\/+$/,
      '',
    );
    const listUrl = `${minioBase}/geoviewer-public/demo?list-type=2`;
    try {
      const res = await this.fetchImpl(listUrl, { headers: { accept: 'application/xml' } });
      if (!res.ok) return [];
      const text = await res.text();
      const xml = new DOMParser().parseFromString(text, 'application/xml');
      const keys = Array.from(xml.querySelectorAll('Contents > Key'))
        .map((el) => el.textContent?.trim() ?? '')
        .filter(Boolean);
      const out: Layer[] = [];
      for (const key of keys) {
        const name = key.split('/').pop() ?? key;
        const norm = normalizeId(name);
        const href = `${minioBase}/geoviewer-public/demo/${encodeURIComponent(name)}`;
        if (name.endsWith('.pmtiles')) {
          out.push({
            id: `demo-${norm}`,
            tenantId: this.tenantId,
            stacItemId: `demo-${norm}`,
            kind: 'vector',
            crs: 'EPSG:4326',
            bbox: [55.0, 24.9, 55.6, 25.4],
            endpoints: { pmtiles: href },
            style: { color: '#6ad5ff' },
            visibility: { minZoom: 6, maxZoom: 22 },
            permissions: { roles: ['viewer'], classification: 'public' },
            label: `Demo ${name}`,
          });
          continue;
        }
        if (name.endsWith('.tif') || name.endsWith('.tiff')) {
          const tilesBase = (import.meta.env.VITE_TILES_URL ?? 'http://localhost:8000').replace(
            /\/+$/,
            '',
          );
          out.push({
            id: `demo-${norm}`,
            tenantId: this.tenantId,
            stacItemId: `demo-${norm}`,
            kind: 'raster',
            crs: 'EPSG:3857',
            bbox: [55.0, 24.9, 55.6, 25.4],
            endpoints: {
              tiles: `${tilesBase}/cog/tiles/{z}/{x}/{y}.png?url=${encodeURIComponent(href)}`,
            },
            visibility: { minZoom: 6, maxZoom: 22 },
            permissions: { roles: ['viewer'], classification: 'public' },
            label: `Demo ${name}`,
          });
        }
      }
      this.demoCache = out;
      return out;
    } catch {
      return [];
    }
  }
}

function normalizeId(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function dedupeLayers(layers: Layer[]): Layer[] {
  const byId = new Map<string, Layer>();
  for (const layer of layers) {
    byId.set(layer.id, layer);
  }
  return [...byId.values()];
}

/** Sample dev catalog. Keeps the demo viewer self-sufficient. */
export const SAMPLE_LAYERS: Layer[] = [
  {
    id: 'osm-base',
    tenantId: 'tnt-default',
    stacItemId: 'osm-base',
    kind: 'raster',
    crs: 'EPSG:3857',
    bbox: [-180, -85, 180, 85],
    endpoints: {
      tiles: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    },
    visibility: { minZoom: 0, maxZoom: 19 },
    permissions: { roles: ['viewer'], classification: 'public' },
    label: 'OpenStreetMap (base)',
  },
  {
    id: 'dubai-streets-pmtiles',
    tenantId: 'tnt-default',
    stacItemId: 'dubai-streets-pmtiles',
    kind: 'vector',
    crs: 'EPSG:4326',
    bbox: [55.0, 24.9, 55.6, 25.4],
    endpoints: {
      // Once GVB-006's onboard_dubai.py runs against the legacy folder, this
      // points to the real PMTiles in MinIO. Until then, rendering falls
      // through to the bbox outline.
      pmtiles: `${import.meta.env.VITE_MINIO_BASE_URL ?? 'http://localhost:9000'}/geoviewer-public/demo/dubai-streets.pmtiles`,
    },
    style: { color: '#FF7A00', sourceLayer: 'streets' },
    visibility: { minZoom: 6, maxZoom: 22 },
    permissions: { roles: ['viewer'], classification: 'public' },
    label: 'Dubai streets (PMTiles)',
  },
  {
    id: 'dubai-bbox-marker',
    tenantId: 'tnt-default',
    stacItemId: 'dubai-bbox-marker',
    kind: 'vector',
    crs: 'EPSG:4326',
    bbox: [55.0, 24.9, 55.6, 25.4],
    endpoints: {},
    style: { color: '#3BE494', lineWidth: 2 },
    visibility: { minZoom: 6, maxZoom: 22 },
    permissions: { roles: ['viewer'], classification: 'public' },
    label: 'Dubai bbox (placeholder)',
  },
];
